import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Image, Calendar, MapPin, CheckCircle, XCircle, Plus, Save, Trash, Upload, CropIcon,Edit } from 'lucide-react';

const CoachingProgramDetails = ({ showNotification }) => {
  const { type } = useParams(); // Get type from params (online/offline)
  const navigate = useNavigate();
  const [programDetails, setProgramDetails] = useState(null);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    program_name: '',
    program_type: '',
    start_date: '',
    end_date: '',
    location: '',
    features: [],
    image_urls: [],
    thumbnail: '' // Added thumbnail field
  });
  const [newFeature, setNewFeature] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null); // Added thumbnailFile state
  const [thumbnailPreview, setThumbnailPreview] = useState(null); // Added thumbnailPreview state
  const [saving, setSaving] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false); // Added upload status for thumbnail

  useEffect(() => {
    if (type) {
      fetchProgramDetailsByType();
    } else {
      setError('Program type not specified');
      setLoading(false);
    }
  }, [type]);

  // Set initial form data when programDetails changes
  useEffect(() => {
    if (programDetails) {
      setFormData({
        program_name: programDetails.program_name || '',
        program_type: programDetails.program_type || '',
        start_date: programDetails.start_date || '',
        end_date: programDetails.end_date || '',
        location: programDetails.location || '',
        features: programDetails.features || [],
        image_urls: programDetails.image_urls || [],
        thumbnail: programDetails.thumbnail || '' // Initialize thumbnail from data
      });
    } else if (type) {
      // Initialize with default values
      const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
      
      // Get current date for defaults
      const today = new Date();
      const nextYear = new Date();
      nextYear.setFullYear(today.getFullYear() + 1);
      
      // Format dates as YYYY-MM-DD for input[type="date"]
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      setFormData({
        program_name: `${formattedType} Coaching Program`,
        program_type: formattedType,
        start_date: formatDate(today),
        end_date: formatDate(nextYear),
        location: formattedType === 'Online' ? 'Anywhere' : 'Gurugram, Haryana',
        features: [],
        image_urls: [],
        thumbnail: '' // Initialize empty thumbnail
      });
    }
  }, [programDetails, type]);
  const fetchProgramDetailsByType = async () => {
    setLoading(true);
    try {
      // Capitalize first letter of type to match how it's stored in DB
      const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
      
      // Fetch programs by type
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/coaching-programs-details/type/${formattedType}`);
      
      if (response.data && response.data.length > 0) {
        // Use the first program of this type (usually there should be only one)
        setProgramDetails(response.data[0]);
        
        // Try to find the matching basic program info
        try {
          const programsResponse = await axios.get('https://apissc.sainikschoolcadet.com/api/coaching-programs');
          const matchingProgram = programsResponse.data.find(p => p.tag === formattedType);
          if (matchingProgram) {
            setProgram(matchingProgram);
          } else {
            // Create a minimal program object with the type
            setProgram({
              id: 'unknown',
              tag: formattedType,
              display_order: 0,
              active: true
            });
          }
        } catch (programErr) {
          console.error('Error fetching programs:', programErr);
          // Create a minimal program object with the type
          setProgram({
            id: 'unknown',
            tag: formattedType,
            display_order: 0,
            active: true
          });
        }
      } else {
        // No program details found, but don't show error - we'll create one
        console.log(`No ${formattedType} program details found, will allow creation`);
        // Create a minimal program object with the type
        setProgram({
          id: 'unknown',
          tag: formattedType,
          display_order: 0,
          active: true
        });
        setProgramDetails(null);
        setIsEditing(true); // Automatically go into edit mode
      }
    } catch (err) {
      console.error('Error fetching program details by type:', err);
      // If 404, we'll create a new one
      if (err.response && err.response.status === 404) {
        const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
        setProgram({
          id: 'unknown',
          tag: formattedType,
          display_order: 0,
          active: true
        });
        setProgramDetails(null);
        setIsEditing(true); // Automatically go into edit mode
      } else {
        setError(`Failed to load ${type} program details`);
        if (typeof showNotification === 'function') {
          showNotification(`Failed to load ${type} program details`, 'error');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleDeleteFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler for thumbnail file change
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;
    
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('title', `${type.charAt(0).toUpperCase() + type.slice(1)} Coaching Program Image`);
      formData.append('tag', 'coaching-program-details');
      
      const response = await axios.post(
        'https://apissc.sainikschoolcadet.com/api/images',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data && response.data.path) {
        // Add the new image URL to the form data
        setFormData(prev => ({
          ...prev,
          image_urls: [...prev.image_urls, response.data.path]
        }));
        
        // Clear the file input and preview
        setImageFile(null);
        setImagePreview(null);
        
        if (typeof showNotification === 'function') {
          showNotification('Image uploaded successfully');
        }
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      if (typeof showNotification === 'function') {
        showNotification('Failed to upload image', 'error');
      }
    }
  };

  // Handler for thumbnail upload
  const handleThumbnailUpload = async () => {
    if (!thumbnailFile) return;
    
    setUploadingThumbnail(true);
    try {
      const thumbnailFormData = new FormData();
      thumbnailFormData.append('image', thumbnailFile);
      thumbnailFormData.append('title', `${type.charAt(0).toUpperCase() + type.slice(1)} Coaching Program Thumbnail (382x194)`);
      thumbnailFormData.append('tag', 'coaching-program-thumbnail');
      
      const response = await axios.post(
        'https://apissc.sainikschoolcadet.com/api/images',
        thumbnailFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data && response.data.path) {
        // Set the thumbnail URL in the form data
        setFormData(prev => ({
          ...prev,
          thumbnail: response.data.path
        }));
        
        // Clear the file input and preview
        setThumbnailFile(null);
        
        if (typeof showNotification === 'function') {
          showNotification('Thumbnail uploaded successfully');
        }
      }
    } catch (err) {
      console.error('Error uploading thumbnail:', err);
      if (typeof showNotification === 'function') {
        showNotification('Failed to upload thumbnail', 'error');
      }
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleDeleteImage = (index) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index)
    }));
  };

  // Handler for deleting thumbnail
  const handleDeleteThumbnail = () => {
    setFormData(prev => ({
      ...prev,
      thumbnail: ''
    }));
    setThumbnailPreview(null);
  };

  const handleSave = async () => {
    // Validate form
    console.log('Form data:', formData);
    if (!formData.program_name || !formData.program_type || !formData.start_date || !formData.end_date) {
      if (typeof showNotification === 'function') {
        showNotification('Please fill all required fields', 'error');
      }
      return;
    }
    
    // Check if thumbnail is provided
    if (!formData.thumbnail) {
      if (typeof showNotification === 'function') {
        showNotification('Please upload a thumbnail image (382x194)', 'error');
      }
      return;
    }
    
    setSaving(true);
    try {
      let response;
      const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
      
      if (programDetails && programDetails.program_id) {
        // Update existing record
        response = await axios.put(
          `https://apissc.sainikschoolcadet.com/api/coaching-programs-details/${programDetails.program_id}`,
          formData
        );
      } else {
        // Create new record
        response = await axios.post(
          'https://apissc.sainikschoolcadet.com/api/coaching-programs-details',
          formData
        );
      }
      
      if (response.data) {
        setProgramDetails(response.data);
        setIsEditing(false);
        if (typeof showNotification === 'function') {
          showNotification(programDetails ? 'Program details updated successfully' : 'Program details created successfully');
        }
      }
    } catch (err) {
      console.error('Error saving program details:', err);
      if (typeof showNotification === 'function') {
        showNotification('Failed to save program details', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error && !isEditing) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex space-x-3">
            <button 
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Go Back
            </button>
            <button 
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create New
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header with back button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="mr-4 p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-slate-900">
            {program?.tag || type} Coaching Program Details
          </h1>
        </div>
        
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {programDetails ? 'Edit Details' : 'Create Details'}
          </button>
        ) : (
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setIsEditing(false);
                if (programDetails) {
                  // Reset form to current details
                  setFormData({
                    program_name: programDetails.program_name || '',
                    program_type: programDetails.program_type || '',
                    start_date: programDetails.start_date || '',
                    end_date: programDetails.end_date || '',
                    location: programDetails.location || '',
                    features: programDetails.features || [],
                    image_urls: programDetails.image_urls || [],
                    thumbnail: programDetails.thumbnail || ''
                  });
                }
                // Clear file previews
                setThumbnailPreview(null);
                setThumbnailFile(null);
                setImagePreview(null);
                setImageFile(null);
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Save
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Program basic info */}
      

      {isEditing ? (
        // Edit/Create Form
        <div className="space-y-6">
          {/* Thumbnail Section */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">
              Program Thumbnail <span className="text-red-500">*</span>
              <span className="text-sm font-normal text-gray-500 ml-2">(Required dimension: 382x194)</span>
            </h2>
            
            <div className="flex flex-col items-center md:flex-row md:items-start">
              {/* Current thumbnail or preview */}
              <div className="mb-4 md:mb-0 md:mr-6 w-full md:w-auto">
                {thumbnailPreview || formData.thumbnail ? (
                  <div className="relative group">
                    <img 
                      src={thumbnailPreview || formData.thumbnail}
                      alt="Thumbnail"
                      className="w-[382px] h-[194px] object-cover rounded-lg border bg-white"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/382x194?text=Thumbnail+Error';
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                      <button
                        onClick={handleDeleteThumbnail}
                        className="p-2 bg-red-500 text-white rounded-full"
                        title="Remove thumbnail"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 text-xs rounded">
                      382 x 194
                    </div>
                  </div>
                ) : (
                  <div className="w-[382px] h-[194px] border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg flex flex-col items-center justify-center text-gray-400">
                    <CropIcon size={32} className="mb-2" />
                    <p className="text-sm">No thumbnail uploaded</p>
                    <p className="text-xs">Required dimension: 382x194</p>
                  </div>
                )}
              </div>
              
              {/* Upload controls */}
              <div className="w-full md:w-80">
                <div className="border border-gray-300 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Thumbnail
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    className="w-full text-sm p-1 border rounded"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Please ensure the image is 382x194 pixels
                  </p>
                  
                  {thumbnailFile && (
                    <button
                      onClick={handleThumbnailUpload}
                      disabled={uploadingThumbnail}
                      className="mt-3 w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center disabled:bg-blue-300"
                    >
                      {uploadingThumbnail ? (
                        <>
                          <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={16} className="mr-2" />
                          Upload Thumbnail
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Program Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Program Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="program_name" className="block mb-1 font-medium">
                  Program Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="program_name"
                  name="program_name"
                  value={formData.program_name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded border-gray-300"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="program_type" className="block mb-1 font-medium">
                  Program Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="program_type"
                  name="program_type"
                  value={formData.program_type}
                  className="w-full p-2 border rounded border-gray-300"
                  disabled
                >
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Type is determined by the URL</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="start_date" className="block mb-1 font-medium">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded border-gray-300"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="end_date" className="block mb-1 font-medium">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded border-gray-300"
                  required
                />
              </div>
              
              <div className="form-group md:col-span-2">
                <label htmlFor="location" className="block mb-1 font-medium">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded border-gray-300"
                />
              </div>
            </div>
          </div>
          
          {/* Features Section */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Program Features</h2>
            
            {formData.features.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No features added yet. Add features below.
              </div>
            ) : (
              <ul className="space-y-2 mb-4">
                {formData.features.map((feature, index) => (
                  <li key={index} className="flex items-start p-3 bg-white border rounded-lg">
                    <div className="flex-shrink-0 mr-3 mt-1">
                      <CheckCircle size={16} className="text-green-500" />
                    </div>
                    <div className="flex-grow">{feature}</div>
                    <button
                      onClick={() => handleDeleteFeature(index)}
                      className="ml-2 text-red-500 hover:text-red-700 focus:outline-none"
                      title="Remove feature"
                    >
                      <Trash size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            
            <div className="flex mt-4">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Enter new feature..."
                className="flex-grow p-2 border rounded-l border-gray-300"
              />
              <button
                onClick={handleAddFeature}
                disabled={!newFeature.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-r hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
              >
                <Plus size={18} className="mr-1" />
                Add
              </button>
            </div>
          </div>
          
          {/* Program Images Section */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Program Images</h2>
            
            {formData.image_urls.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No images added yet. Upload images below.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                {formData.image_urls.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Program image ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg border"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/150?text=Image+Error';
                      }}
                    />
                    <button
                      onClick={() => handleDeleteImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove image"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-4">
              <div className="border-dashed border-2 border-gray-300 rounded-lg p-4">
                <div className="flex flex-col items-center">
                  <Upload size={24} className="text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Upload a new image</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full"
                  />
                  
                  {imagePreview && (
                    <div className="mt-3 w-full">
                      <p className="text-sm font-medium mb-1">Preview:</p>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-40 h-40 mx-auto object-cover border rounded"
                      />
                      <div className="flex justify-center mt-2">
                        <button
                          onClick={handleImageUpload}
                          className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                        >
                          <Upload size={16} className="mr-1" />
                          Upload Image
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // View Mode
        <>
          {/* Display program details if available */}
          {programDetails ? (
            <>
              {/* Thumbnail Display */}
              {programDetails.thumbnail && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4">Program Thumbnail</h2>
                  <div className="bg-gray-50 rounded-lg p-6 flex justify-center">
                    <img 
                      src={programDetails.thumbnail}
                      alt={`${programDetails.program_type} Program Thumbnail`}
                      className="w-[382px] h-[194px] object-cover rounded-lg border shadow"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/382x194?text=Thumbnail+Error';
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Date and Location Section */}
              {(programDetails.start_date || programDetails.end_date || programDetails.location) && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4">Program Information</h2>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {programDetails.start_date && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mt-1 mr-3">
                            <Calendar size={18} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">Start Date</p>
                            <p className="text-gray-600">{new Date(programDetails.start_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      )}
                      
                      {programDetails.end_date && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mt-1 mr-3">
                            <Calendar size={18} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">End Date</p>
                            <p className="text-gray-600">{new Date(programDetails.end_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      )}
                      
                      {programDetails.location && (
                        <div className="flex items-start md:col-span-2">
                          <div className="flex-shrink-0 mt-1 mr-3">
                            <MapPin size={18} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">Location</p>
                            <p className="text-gray-600">{programDetails.location}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Features Section */}
              {programDetails.features && programDetails.features.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4">Key Features</h2>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <ul className="space-y-2">
                      {programDetails.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <div className="flex-shrink-0 mr-2 mt-1 text-blue-600">•</div>
                          <div>{feature}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Images Section */}
              {programDetails.image_urls && programDetails.image_urls.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4">Program Images</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {programDetails.image_urls.map((url, index) => (
                      <div key={index} className="bg-gray-50 p-2 rounded-lg">
                        <img
                          src={url}
                          alt={`Program image ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/150?text=Image+Error';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center mb-8">
              <p className="text-yellow-700">No details exist for this program type yet.</p>
              <button
                onClick={() => setIsEditing(true)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center"
              >
                <Plus size={18} className="mr-2" />
                Create Program Details
              </button>
            </div>
          )}
        </>
      )}

      
    </div>
  );
};

export default CoachingProgramDetails;